import React, { useEffect, useState, useMemo } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import { Dialog, Button, Input, Label, Checkbox, Tooltip, Alert, Combobox, RadioGroup } from "@/components/ui";
import {
  DigestSubscription,
  DigestSubscriptionRequest,
  DigestSubscriptionServices,
  DigestComponentOption,
  DigestFrequency,
} from "@/services/bedrock";
import { AssistantServices } from "@/services/AssistantServices";
import { fullNameFrom, useSocialCoachSession } from "@/utils";
import { Trash2, Info, Check, User, Users, X, Clock, Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface DigestSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription: DigestSubscription | null;
  fixedFrequency?: DigestFrequency;
}

const DAYS_OF_WEEK = [
  { value: "0", label: "Sunday", short: "Su" },
  { value: "1", label: "Monday", short: "Mo" },
  { value: "2", label: "Tuesday", short: "Tu" },
  { value: "3", label: "Wednesday", short: "We" },
  { value: "4", label: "Thursday", short: "Th" },
  { value: "5", label: "Friday", short: "Fr" },
  { value: "6", label: "Saturday", short: "Sa" },
];

export const DigestSubscriptionDialog: React.FC<DigestSubscriptionDialogProps> = ({
  open,
  onOpenChange,
  subscription,
  fixedFrequency,
}) => {
  const queryClient = useQueryClient();
  const { session, coachId, isMainCoach, userId: currentUserId } = useSocialCoachSession();
  const isEdit = !!subscription;
  const frequency = fixedFrequency || subscription?.frequency || "weekly";

  const [isCustomizingMonthly, setIsCustomizingMonthly] = useState(false);
  const [isCustomizingComponents, setIsCustomizingComponents] = useState(false);
  const [subscriberType, setSubscriberType] = useState<"primary" | "me" | "select">("primary");
  const [showCustomCard, setShowCustomCard] = useState(false);
  const [destroyedRecipientIds, setDestroyedRecipientIds] = useState<number[]>([]);

  // Fetch Assistants
  const { data: assistants } = useQuery({
    queryKey: ["assistants", coachId],
    queryFn: () => AssistantServices.getByCoachId(coachId?.toString() || ""),
    enabled: !!coachId,
  });

  const assistantOptions = useMemo(() => {
    if (!assistants) return [];
    return assistants.map(a => ({
      userId: a.userId,
      name: fullNameFrom(a.account.profile),
      email: a.account.user.email,
    }));
  }, [assistants]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<DigestSubscriptionRequest>({
    defaultValues: {
      frequency,
      enabledComponents: [],
      digestRecipientsAttributes: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "digestRecipientsAttributes",
  });

  const formRecipients = watch("digestRecipientsAttributes");

  const owners = useMemo(() => formRecipients?.filter(r => r.isOwner) || [], [formRecipients]);
  const isMultiOwner = owners.length > 1;

  const selectedAssistants = useMemo(() => {
    return assistantOptions.filter(a => owners.some(o => o.email === a.email));
  }, [assistantOptions, owners]);

  const primarySubscriberEmail = useMemo(() => {
    if (!formRecipients || formRecipients.length === 0) return session?.email || "";
    return formRecipients.find(r => r.isOwner)?.email || session?.email || "";
  }, [formRecipients, session?.email]);

  const { data: options, isLoading: isLoadingOptions } = useQuery({
    queryKey: ["digestOptions", primarySubscriberEmail, frequency],
    queryFn: () =>
      DigestSubscriptionServices.getOptions({
        email: primarySubscriberEmail,
        frequency,
      }),
    enabled: !!primarySubscriberEmail && !!frequency,
  });

  useEffect(() => {
    if (subscription) {
      const isCustomDay =
        subscription.frequency === "monthly" && subscription.dayOfMonth != null && subscription.dayOfMonth !== 1;
      setIsCustomizingMonthly(isCustomDay);

      const hasCustomComponents = (subscription.enabledComponents?.length ?? 0) > 0;
      setIsCustomizingComponents(hasCustomComponents);

      const owner = subscription.digestRecipients?.find(r => r.isOwner);
      const isMe = owner?.email === session?.email;

      if (isMainCoach) {
        if (isMe) {
          setSubscriberType("me");
          setShowCustomCard(false);
        } else {
          setSubscriberType("select");
          setShowCustomCard(true);
        }
      } else {
        if (isMe) {
          setSubscriberType("me");
          setShowCustomCard(false);
        } else if (owner) {
          setSubscriberType("select");
          setShowCustomCard(true);
        } else {
          setSubscriberType("primary");
          setShowCustomCard(false);
        }
      }

      let formattedTime = "";
      if (subscription.timeOfDay) {
        const timeStr = subscription.timeOfDay;
        const m = timeStr.includes("T")
          ? moment.tz(timeStr, "America/Los_Angeles")
          : moment.tz(timeStr, ["HH:mm:ss", "HH:mm"], "America/Los_Angeles");
        formattedTime = m.local().format("HH:mm");
      }

      reset({
        frequency: subscription.frequency,
        dayOfWeek: subscription.dayOfWeek ?? undefined,
        dayOfMonth: subscription.dayOfMonth ?? 1,
        timeOfDay: formattedTime || undefined,
        enabledComponents: subscription.enabledComponents || [],
        digestRecipientsAttributes:
          subscription.digestRecipients?.map(r => ({
            id: r.digestRecipientId,
            email: r.email,
            name: r.name,
            active: r.active,
            isOwner: r.isOwner,
          })) || [],
      });
      setDestroyedRecipientIds([]);
    } else {
      reset({
        frequency,
        enabledComponents: [],
        digestRecipientsAttributes: [
          { email: session?.email || "", name: session?.fullName || "", active: true, isOwner: true },
        ],
        dayOfMonth: 1,
        dayOfWeek: 1, // Monday
        timeOfDay: "09:00",
        userId: currentUserId,
      });
      setIsCustomizingComponents(false);
      setIsCustomizingMonthly(false);
      setSubscriberType(isMainCoach ? "me" : "me"); // Both default to 'me' now
      setShowCustomCard(false);
    }
  }, [subscription, reset, session, frequency, isMainCoach]);

  const mutation = useMutation({
    mutationFn: async (data: DigestSubscriptionRequest | DigestSubscriptionRequest[]) => {
      if (isEdit) {
        return DigestSubscriptionServices.updateSubscription(
          subscription!.digestSubscriptionId,
          data as DigestSubscriptionRequest
        );
      }
      if (Array.isArray(data)) {
        const results = await Promise.all(data.map(d => DigestSubscriptionServices.createSubscription(d)));
        return results[0];
      }
      return DigestSubscriptionServices.createSubscription(data as DigestSubscriptionRequest);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["digestSubscriptions"] });
      const count = Array.isArray(variables) ? variables.length : 1;
      const message = isEdit
        ? "Subscription updated"
        : count > 1
        ? `${count} subscriptions created`
        : "Subscription created";
      toast.success(message);
      onOpenChange(false);
    },
    onError: (err: any) => {
      console.error("Failed to save subscription", err);
      toast.error("Failed to save subscription");
    },
  });

  const onSubmit = (data: DigestSubscriptionRequest) => {
    const currentOwners = data.digestRecipientsAttributes?.filter(r => r.isOwner) || [];
    if (currentOwners.length === 0) {
      toast.error("Please select a primary subscriber");
      return;
    }

    if (isEdit) {
      const payload = { ...data };
      if (destroyedRecipientIds.length > 0) {
        payload.digestRecipientsAttributes = [
          ...(payload.digestRecipientsAttributes || []),
          ...destroyedRecipientIds.map(
            id =>
              ({
                id,
                active: false,
              } as any)
          ),
        ];
      }
      if (payload.timeOfDay) {
        payload.timeOfDay = moment(payload.timeOfDay, "HH:mm").tz("America/Los_Angeles").format("HH:mm:ss");
      }
      mutation.mutate(payload);
    } else {
      if (currentOwners.length > 1) {
        const payloads = currentOwners.map(owner => {
          const assistant = assistantOptions.find(a => a.email === owner.email);
          const p = {
            ...data,
            userId: assistant?.userId,
            digestRecipientsAttributes: [owner],
          };
          if (p.timeOfDay) {
            p.timeOfDay = moment(p.timeOfDay, "HH:mm").tz("America/Los_Angeles").format("HH:mm:ss");
          }
          return p;
        });
        mutation.mutate(payloads as any);
      } else {
        const payload = { ...data };
        const owner = currentOwners[0];
        if (subscriberType === "select") {
          const assistant = assistantOptions.find(a => a.email === owner.email);
          payload.userId = assistant?.userId;
        }
        if (payload.timeOfDay) {
          payload.timeOfDay = moment(payload.timeOfDay, "HH:mm").tz("America/Los_Angeles").format("HH:mm:ss");
        }
        mutation.mutate(payload);
      }
    }
  };

  const handleSubscriberTypeChange = (type: "primary" | "me" | "select") => {
    setSubscriberType(type);
    setShowCustomCard(false);

    const newRecipients = (formRecipients || []).filter(r => !r.isOwner);

    let email = "";
    let name = "";
    let targetUserId: number | undefined;

    if (type === "me" && session) {
      email = session.email;
      name = session.fullName || "";
      targetUserId = currentUserId;
    }

    setValue("userId", targetUserId);

    if (email) {
      newRecipients.unshift({ email, name, active: true, isOwner: true });
      setValue("digestRecipientsAttributes", newRecipients);
    } else if (type === "select") {
      setValue("digestRecipientsAttributes", newRecipients);
    }
  };

  const labels: Record<string, string> = {
    daily: "Daily Subscription",
    weekly: "Weekly Subscription",
    monthly: "Monthly Subscription",
  };

  const additionalRecipients = formRecipients?.filter(r => !r.isOwner);
  const currentOwner = formRecipients?.find(r => r.isOwner);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl">
      <Dialog.Content className="p-0 flex flex-col max-h-[95vh]">
        <Dialog.Header className="px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              {frequency === "daily" ? <Clock className="size-5" /> : <Calendar className="size-5" />}
            </div>
            <div>
              <Dialog.Title>
                {isEdit ? "Edit" : "New"} {labels[frequency]}
              </Dialog.Title>
              <Dialog.Description className="text-xs">
                Configure your automated {labels[frequency].toLowerCase()} subscription.
              </Dialog.Description>
            </div>
          </div>
        </Dialog.Header>

        <div className="flex-1 overflow-y-auto px-6 py-8">
          <form id="digest-subscription-form" onSubmit={handleSubmit(onSubmit)} className="space-y-12">
            {/* SECTION 1: RECIPIENTS */}
            <section className="space-y-8">
              <div className="space-y-6">
                <div className="space-y-1">
                  <Label className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <User className="size-4 text-primary" /> 1. Primary Subscriber
                  </Label>
                  <p className="text-[11px] text-slate-500">
                    The subscription data will be filtered based on this user's permissions.
                  </p>
                </div>

                <RadioGroup
                  value={subscriberType}
                  onValueChange={(val: any) => handleSubscriberTypeChange(val)}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  <Label
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all",
                      subscriberType === "me"
                        ? "border-primary bg-primary/5 ring-1 ring-primary shadow-xs"
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <RadioGroup.Item value="me" />
                    <div className="flex flex-col">
                      <span className="text-sm font-bold text-slate-900">This one's for me</span>
                      <span className="text-xs text-slate-500 truncate max-w-[150px]">{session?.email}</span>
                    </div>
                  </Label>

                  <Label
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all",
                      subscriberType === "select"
                        ? "border-primary bg-primary/5 ring-1 ring-primary shadow-xs"
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <RadioGroup.Item value="select" />
                    <span className="text-sm font-bold text-slate-900">Select a user</span>
                  </Label>
                </RadioGroup>

                {subscriberType === "select" && (
                  <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                    {showCustomCard && currentOwner ? (
                      <div className="flex items-center justify-between p-4 rounded-xl border border-primary bg-primary/5 shadow-sm">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-slate-900">{currentOwner.name}</span>
                          <span className="text-xs text-slate-500">{currentOwner.email}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          className="text-slate-400 hover:text-slate-600 h-8 w-8"
                          onClick={() => setShowCustomCard(false)}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    ) : (
                      <Combobox
                        items={assistantOptions}
                        placeholder="Search assistants..."
                        itemToStringLabel={i => i.name}
                        multiple={!isEdit}
                        value={selectedAssistants}
                        onValueChange={(val: any) => {
                          const selectedItems = Array.isArray(val) ? val : val ? [val] : [];
                          const currentCCs = (formRecipients || []).filter(r => !r.isOwner);
                          const newOwners = selectedItems.map(item => ({
                            email: item.email,
                            name: item.name,
                            active: true,
                            isOwner: true,
                          }));

                          if (selectedItems.length > 1) {
                            // Multiple owners, remove CCs
                            setValue("digestRecipientsAttributes", newOwners);
                          } else {
                            // Single or no owner, keep CCs
                            setValue("digestRecipientsAttributes", [...newOwners, ...currentCCs]);
                            if (selectedItems.length === 1) {
                              setValue("userId", selectedItems[0].userId);
                            }
                          }
                        }}
                        className="w-full"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* INTEGRATED CC SECTION */}
              {!isMultiOwner && (
                <div className="space-y-4 pt-2">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Users className="size-4 text-slate-400" />
                      <span className="text-sm font-semibold text-slate-700">Want to include others?</span>
                      <Tooltip>
                        <Tooltip.Trigger
                          render={props => <Info {...props} className="size-3.5 text-slate-300 cursor-help" />}
                        />
                        <Tooltip.Content className="max-w-xs">
                          CC recipients receive an identical copy of the report, seeing the same data that the primary
                          subscriber does.
                        </Tooltip.Content>
                      </Tooltip>
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs font-semibold text-primary hover:no-underline"
                      onClick={() => append({ email: "", name: "", active: true, isOwner: false })}
                    >
                      Add CC
                    </Button>
                  </div>

                  {additionalRecipients && additionalRecipients.length > 0 && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                      {fields.map((field, index) => {
                        if (formRecipients && formRecipients[index]?.isOwner) return null;
                        const hasError = !!errors.digestRecipientsAttributes?.[index]?.email;

                        return (
                          <div key={field.id} className="space-y-1">
                            <div
                              className={cn(
                                "flex items-center gap-3 p-3 rounded-xl border transition-all bg-white shadow-xs",
                                hasError ? "border-destructive ring-1 ring-destructive/20" : "border-slate-200"
                              )}
                            >
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                                <Input
                                  {...register(`digestRecipientsAttributes.${index}.name`)}
                                  placeholder="Name"
                                  className="bg-slate-50/50 border-slate-100 h-8 text-xs rounded-lg"
                                />
                                <Input
                                  type="email"
                                  {...register(`digestRecipientsAttributes.${index}.email`, {
                                    required: "Email is required",
                                    pattern: {
                                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                      message: "Invalid email address",
                                    },
                                  })}
                                  placeholder="Email"
                                  className="bg-slate-50/50 border-slate-100 h-8 text-xs rounded-lg"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                className="text-slate-300 hover:text-destructive h-8 w-8"
                                onClick={() => {
                                  const recipient = formRecipients?.[index];
                                  if (recipient?.id) {
                                    setDestroyedRecipientIds(prev => [...prev, recipient.id!]);
                                  }
                                  remove(index);
                                }}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            </div>
                            {hasError && (
                              <p className="text-[10px] text-destructive font-bold px-3 animate-in fade-in slide-in-from-top-1 duration-200">
                                {errors.digestRecipientsAttributes?.[index]?.email?.message}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* SECTION 2: TIMING */}
            <section className="space-y-4">
              <Label className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Clock className="size-4 text-primary" /> 2. Delivery Schedule
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50/50 p-6 rounded-2xl border border-slate-100 shadow-xs">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-slate-700">Time of Day</Label>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-1.5 py-0.5 rounded-sm">
                      {moment.tz(moment.tz.guess()).format("z")}
                    </span>
                  </div>
                  <Input
                    type="time"
                    {...register("timeOfDay")}
                    className="bg-white rounded-lg h-10 border-slate-200 shadow-xs"
                  />
                </div>

                {frequency === "weekly" && (
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold text-slate-700">Preferred Day</Label>
                    <Controller
                      control={control}
                      name="dayOfWeek"
                      render={({ field }) => (
                        <div className="flex flex-wrap gap-2">
                          {DAYS_OF_WEEK.map(day => {
                            const isSelected = (field.value ?? 0) === parseInt(day.value);
                            return (
                              <Button
                                key={day.value}
                                type="button"
                                variant={isSelected ? "default" : "outline"}
                                size="icon"
                                className={cn(
                                  "size-9 rounded-full text-[11px] font-bold transition-all",
                                  isSelected
                                    ? "bg-primary text-primary-foreground shadow-sm ring-2 ring-primary ring-offset-1 border-primary"
                                    : "text-slate-500 hover:text-slate-900 border-slate-200"
                                )}
                                onClick={() => field.onChange(parseInt(day.value))}
                              >
                                {day.short}
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    />
                  </div>
                )}

                {frequency === "monthly" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-slate-700">Delivery Day</Label>
                      <button
                        type="button"
                        onClick={() => {
                          const willBeCustomizing = !isCustomizingMonthly;
                          setIsCustomizingMonthly(willBeCustomizing);
                          if (!willBeCustomizing) {
                            setValue("dayOfMonth", 1);
                          }
                        }}
                        className="text-[10px] font-bold text-primary hover:underline uppercase tracking-wider"
                      >
                        {isCustomizingMonthly ? "Reset to 1st" : "Customize"}
                      </button>
                    </div>
                    {!isCustomizingMonthly ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100 shadow-xs">
                        <Check className="size-3.5" />
                        <span className="text-xs font-medium">1st of the month (Standard)</span>
                      </div>
                    ) : (
                      <Input
                        type="number"
                        min={1}
                        max={31}
                        {...register("dayOfMonth", { valueAsNumber: true })}
                        className="bg-white rounded-lg h-10 border-slate-200 shadow-xs"
                        placeholder="Day (1-31)"
                      />
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* SECTION 3: CONTENT */}
            <section className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <Label className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Check className="size-4 text-primary" /> 3. Report Content
                  </Label>
                </div>

                <div className="flex bg-slate-100 p-1 rounded-xl w-fit shadow-inner shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomizingComponents(false);
                      setValue("enabledComponents", []);
                    }}
                    className={cn(
                      "px-4 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded-lg transition-all",
                      !isCustomizingComponents
                        ? "bg-white shadow-sm text-primary"
                        : "text-slate-500 hover:text-slate-700"
                    )}
                  >
                    Recommended
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCustomizingComponents(true)}
                    className={cn(
                      "px-4 py-1.5 text-[10px] uppercase tracking-wider font-bold rounded-lg transition-all",
                      isCustomizingComponents
                        ? "bg-white shadow-sm text-primary"
                        : "text-slate-500 hover:text-slate-700"
                    )}
                  >
                    Custom
                  </button>
                </div>
              </div>

              {!isCustomizingComponents ? (
                <div className="space-y-6 animate-in fade-in duration-300">
                  {isLoadingOptions ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 pl-1">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex gap-3 items-start animate-pulse">
                          <div className="mt-1 size-4 rounded bg-slate-200 shrink-0" />
                          <div className="space-y-2 flex-1">
                            <div className="h-3 bg-slate-200 rounded w-1/3" />
                            <div className="h-2 bg-slate-100 rounded w-full" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    options &&
                    options.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 pl-1">
                        {options.map((opt: DigestComponentOption) => (
                          <div key={opt.key} className="flex gap-3 items-start">
                            <div className="mt-0.5 size-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 shadow-xs">
                              <Check className="size-2.5" strokeWidth={3} />
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-xs font-bold text-slate-900 block leading-tight">{opt.name}</span>
                              <p className="text-[10px] text-slate-500 leading-relaxed">{opt.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}

                  <Alert className="bg-emerald-50/50 border-emerald-100 text-emerald-900 shadow-none rounded-2xl">
                    <RefreshCw className="size-4 text-emerald-600" />
                    <p className="text-xs">
                      <strong>Auto-updating enabled:</strong> New report features and modules will be automatically
                      added to your subscription as they are released.
                    </p>
                  </Alert>
                </div>
              ) : (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  {isLoadingOptions ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 pl-1">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="flex gap-3 items-start animate-pulse">
                          <div className="mt-1 size-4 rounded bg-slate-200 shrink-0" />
                          <div className="space-y-2 flex-1">
                            <div className="h-3 bg-slate-200 rounded w-1/3" />
                            <div className="h-2 bg-slate-100 rounded w-full" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 pl-1">
                      {options?.map((opt: DigestComponentOption) => (
                        <div key={opt.key} className="flex gap-3 items-start animate-in fade-in duration-300 group">
                          <Controller
                            control={control}
                            name="enabledComponents"
                            render={({ field }) => (
                              <Checkbox
                                id={`comp-${opt.key}`}
                                className="mt-0.5"
                                checked={field.value?.includes(opt.key)}
                                onCheckedChange={checked => {
                                  const newValue = checked
                                    ? [...(field.value || []), opt.key]
                                    : field.value?.filter(k => k !== opt.key);
                                  field.onChange(newValue);
                                }}
                              />
                            )}
                          />
                          <div className="space-y-0.5">
                            <Label
                              htmlFor={`comp-${opt.key}`}
                              className="text-xs font-bold text-slate-900 block leading-tight cursor-pointer group-hover:text-primary transition-colors"
                            >
                              {opt.name}
                            </Label>
                            <p className="text-[10px] text-slate-500 leading-relaxed">{opt.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <Alert className="bg-amber-50/50 border-amber-100 text-amber-900 shadow-none rounded-2xl">
                    <Info className="size-4 text-amber-600" />
                    <p className="text-xs">Newly released components will not be added automatically in custom mode.</p>
                  </Alert>
                </div>
              )}
            </section>
          </form>
        </div>

        <Dialog.Footer className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="digest-subscription-form" loading={mutation.isPending} className="px-8 shadow-sm">
            {isEdit ? "Save Subscription" : "Create Subscription"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};
